import axios from 'axios'
import request from '@/utils/request'

const state = {
  token: localStorage.getItem('token') || null,
  refreshToken: localStorage.getItem('refreshToken') || null,
  tokenExpiresAt: localStorage.getItem('tokenExpiresAt') || null,
  refreshTokenExpiresAt: localStorage.getItem('refreshTokenExpiresAt') || null,
  user: JSON.parse(localStorage.getItem('user')) || null,
  employee: JSON.parse(localStorage.getItem('employee')) || null,
  salesProfile: JSON.parse(localStorage.getItem('salesProfile')) || null,
  permissions: JSON.parse(localStorage.getItem('permissions')) || {
    menu: [],
    func: [],
    groups: [],
    menuTree: []
  }
}

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token
    if (token) {
      localStorage.setItem('token', token)
    } else {
      localStorage.removeItem('token')
    }
  },
  SET_REFRESH_TOKEN(state, refreshToken) {
    state.refreshToken = refreshToken
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken)
    } else {
      localStorage.removeItem('refreshToken')
    }
  },
  SET_TOKEN_EXPIRES_AT(state, expiresAt) {
    state.tokenExpiresAt = expiresAt
    if (expiresAt) {
      localStorage.setItem('tokenExpiresAt', expiresAt)
    } else {
      localStorage.removeItem('tokenExpiresAt')
    }
  },
  SET_REFRESH_TOKEN_EXPIRES_AT(state, expiresAt) {
    state.refreshTokenExpiresAt = expiresAt
    if (expiresAt) {
      localStorage.setItem('refreshTokenExpiresAt', expiresAt)
    } else {
      localStorage.removeItem('refreshTokenExpiresAt')
    }
  },
  SET_USER(state, user) {
    state.user = user
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    } else {
      localStorage.removeItem('user')
    }
  },
  SET_EMPLOYEE(state, employee) {
    state.employee = employee
    if (employee) {
      localStorage.setItem('employee', JSON.stringify(employee))
    } else {
      localStorage.removeItem('employee')
    }
  },
  SET_SALES_PROFILE(state, salesProfile) {
    state.salesProfile = salesProfile
    if (salesProfile) {
      localStorage.setItem('salesProfile', JSON.stringify(salesProfile))
    } else {
      localStorage.removeItem('salesProfile')
    }
  },
  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions
    if (permissions) {
      localStorage.setItem('permissions', JSON.stringify(permissions))
    } else {
      localStorage.removeItem('permissions')
    }
  },
  LOGOUT(state) {
    state.token = null
    state.refreshToken = null
    state.tokenExpiresAt = null
    state.refreshTokenExpiresAt = null
    state.user = null
    state.employee = null
    state.salesProfile = null
    state.permissions = {
      menu: [],
      func: [],
      groups: [],
      menuTree: []
    }
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('tokenExpiresAt')
    localStorage.removeItem('refreshTokenExpiresAt')
    localStorage.removeItem('user')
    localStorage.removeItem('employee')
    localStorage.removeItem('salesProfile')
    localStorage.removeItem('permissions')
  }
}

const actions = {
  async login({ commit }, credentials) {
    const response = await axios.post('/api/auth/login/', credentials)
    const { access, refresh, user, permissions } = response.data

    commit('SET_TOKEN', access)
    commit('SET_REFRESH_TOKEN', refresh)
    commit('SET_USER', user)
    commit('SET_PERMISSIONS', permissions)
    
    const tokenExpiresAt = getTokenExpiresAt(access)
    const refreshTokenExpiresAt = getTokenExpiresAt(refresh)
    commit('SET_TOKEN_EXPIRES_AT', tokenExpiresAt)
    commit('SET_REFRESH_TOKEN_EXPIRES_AT', refreshTokenExpiresAt)
    
    return response.data
  },
  
  async refreshToken({ commit, state }) {
    const refreshToken = state.refreshToken
    if (!refreshToken) {
      throw new Error('没有刷新令牌')
    }

    const response = await axios.post('/api/auth/token/refresh/', { refresh: refreshToken })
    const { access, refresh } = response.data

    commit('SET_TOKEN', access)
    if (refresh) {
      commit('SET_REFRESH_TOKEN', refresh)
    }
    
    const tokenExpiresAt = getTokenExpiresAt(access)
    const refreshTokenExpiresAt = refresh ? getTokenExpiresAt(refresh) : state.refreshTokenExpiresAt
    commit('SET_TOKEN_EXPIRES_AT', tokenExpiresAt)
    commit('SET_REFRESH_TOKEN_EXPIRES_AT', refreshTokenExpiresAt)

    return response.data
  },

  async fetchUser({ commit }) {
    // 使用 request 实例，支持 token 自动续签
    const userData = await request.get('/auth/users/me/')
    commit('SET_USER', userData)

    try {
      const employeeData = await request.get('/organization/employees/my_info/')
      commit('SET_EMPLOYEE', employeeData)
    } catch (error) {
      commit('SET_EMPLOYEE', null)
    }

    try {
      const salesProfileData = await request.get('/customers/sales-profiles/my_profile/')
      commit('SET_SALES_PROFILE', salesProfileData)
    } catch (error) {
      commit('SET_SALES_PROFILE', null)
    }

    return userData
  },
  
  logout({ commit }) {
    commit('LOGOUT')
  }
}

const getters = {
  isAuthenticated: state => !!state.token,
  currentUser: state => state.user,
  currentEmployee: state => state.employee,
  currentSalesProfile: state => state.salesProfile,
  userRole: state => state.salesProfile?.role,
  isManager: state => state.salesProfile?.role === 'sales_manager',
  isSalesperson: state => state.salesProfile?.role === 'salesperson',
  isAdmin: state => state.user?.is_staff || state.user?.is_superuser,
  // 权限相关 getters
  menuPermissions: state => state.permissions?.menu || [],
  funcPermissions: state => state.permissions?.func || [],
  userGroups: state => state.permissions?.groups || [],
  menuTree: state => state.permissions?.menuTree || [],
  hasPermission: state => code => {
    // 超级管理员拥有所有权限
    if (state.user?.is_superuser) return true
    return state.permissions?.func?.includes(code) || false
  },
  hasAnyPermission: state => codes => {
    // 超级管理员拥有所有权限
    if (state.user?.is_superuser) return true
    return codes.some(code => 
      state.permissions?.func?.includes(code) || 
      state.permissions?.menu?.includes(code)
    ) || false
  },
  hasMenuPermission: state => code => {
    // 超级管理员拥有所有权限
    if (state.user?.is_superuser) return true
    return state.permissions?.menu?.includes(code) || false
  },
  hasGroup: state => groupCode => {
    return state.permissions?.groups?.some(g => g.code === groupCode) || false
  },
  isTokenExpiringSoon: state => {
    if (!state.tokenExpiresAt) return false
    const now = Date.now()
    const expiresAt = new Date(state.tokenExpiresAt).getTime()
    const timeUntilExpiry = expiresAt - now
    return timeUntilExpiry <= 5 * 60 * 1000
  },
  isTokenExpired: state => {
    if (!state.tokenExpiresAt) return false
    const now = Date.now()
    const expiresAt = new Date(state.tokenExpiresAt).getTime()
    return now >= expiresAt
  },
  isRefreshTokenExpired: state => {
    if (!state.refreshTokenExpiresAt) return false
    const now = Date.now()
    const expiresAt = new Date(state.refreshTokenExpiresAt).getTime()
    return now >= expiresAt
  }
}

function getTokenExpiresAt(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return new Date(payload.exp * 1000).toISOString()
  } catch (error) {
    return null
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
